import SearchIcon from '@mui/icons-material/Search';
import DescriptionIcon from '@mui/icons-material/Description';
import GroupsIcon from '@mui/icons-material/Groups';
import StarsIcon from '@mui/icons-material/Stars';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { Box, Button, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useSwipeable } from 'react-swipeable';
import { useNavigate } from 'react-router-dom';
import ReformersHeroImage from '../assets/images/hero-home-page-robert-lincoln.png';
import HeroImage from '../assets/images/hero-1-robert-testifying.png';
import CoinHeroDesktop from '../assets/images/coin-collection-hero-desktop.png';
import CoinHeroMobile from '../assets/images/coin-collection-hero-mobile.png';
import BillConcernDialog from './BillConcernDialog';
import { trackEvent } from './GoogleAnalytics';

// ─── Types ────────────────────────────────────────────────────────────────────

interface HeroSlideCTA {
  label: string;
  action: 'navigate' | 'dialog';
  dialogType?: 'joinTeam' | 'calendly' | 'billConcern';
  variant?: 'contained' | 'outlined';
  destination?: string;
  analyticsEvent?: string;
  icon: React.ReactNode;
}

interface HeroSlide {
  id: string;
  heading: string;
  subheading?: string;
  bodyText: string;
  ctas: HeroSlideCTA[];
  desktopImage: string;
  mobileImage?: string;
  imageAlt: string;
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface HeroCarouselProps {
  onCalendlyOpen: () => void;
  onJoinTeamOpen: () => void;
}

// ─── Slide data ───────────────────────────────────────────────────────────────

const buildSlides = (): HeroSlide[] => [
  {
    id: 'garza-hero',
    heading: 'Championing Parental Equality & Rights',
    subheading: 'International Family Court Reform',
    bodyText: 'Join Robert Garza in the fight for fair family courts and parental rights across the world.',
    desktopImage: HeroImage,
    imageAlt: 'Robert Garza',
    ctas: [
      {
        label: 'Explore Bills',
        action: 'navigate',
        destination: '/family-court-reform-bills',
        analyticsEvent: 'Explore Bills - Home Hero',
        icon: <DescriptionIcon />,
      },
      {
        label: 'Find a Bill for Your Concern',
        action: 'dialog',
        dialogType: 'billConcern',
        variant: 'outlined',
        icon: <SearchIcon />,
      },
    ],
  },
  {
    id: 'reformers',
    heading: 'The Reformers',
    bodyText:
      'Join Robert Garza and Lincoln Brubaker every week for strategy calls on how you can make legislative changes in your community.',
    desktopImage: ReformersHeroImage,
    imageAlt: 'Robert Garza and fellow reformers at a strategy meeting',
    ctas: [
      {
        label: 'Join the Movement',
        action: 'dialog',
        dialogType: 'joinTeam',
        analyticsEvent: 'Join the Team - Home Hero',
        icon: <GroupsIcon />,
      },
    ],
  },
  {
    id: 'commemorative-coins',
    heading: 'Carry the Movement Forward',
    bodyText: 'Stand for justice. Strengthen families. Help turn advocacy into action.',
    desktopImage: CoinHeroDesktop,
    mobileImage: CoinHeroMobile,
    imageAlt: 'Family rights commemorative challenge coin collection',
    ctas: [
      {
        label: 'Explore the Coin Collection',
        action: 'navigate',
        destination: '/commemorative-coins',
        analyticsEvent: 'Explore Coin Collection - Home Hero',
        icon: <StarsIcon />,
      },
    ],
  },
];

// ─── Component ────────────────────────────────────────────────────────────────

const SLIDE_COUNT = 3;
const INTERVAL_MS = 6000;

const HeroCarousel: React.FC<HeroCarouselProps> = ({ onCalendlyOpen, onJoinTeamOpen }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTabletOrBelow = useMediaQuery(theme.breakpoints.down('md'));
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

  const [activeSlide, setActiveSlide] = useState<number>(0);
  const [isPaused, setIsPaused] = useState<boolean>(false);
  const [hasFocus, setHasFocus] = useState(false);
  const [rotationStopped, setRotationStopped] = useState(false);
  const [concernDialogOpen, setConcernDialogOpen] = useState(false);
  const navigate = useNavigate();

  const FADE_MS = prefersReducedMotion ? 0 : 450;

  const slides = buildSlides();

  // ── Auto-scroll timer (FR-002, FR-003, FR-012, FR-015) ──────────────────────
  useEffect(() => {
    if (isPaused || hasFocus || rotationStopped || isTabletOrBelow || concernDialogOpen || prefersReducedMotion) return;
    const timer = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % SLIDE_COUNT);
    }, INTERVAL_MS);
    return () => clearInterval(timer);
  }, [isPaused, hasFocus, rotationStopped, isTabletOrBelow, concernDialogOpen, prefersReducedMotion]);

  // ── CTA click handler ────────────────────────────────────────────────────────
  const handleCtaClick = (cta: HeroSlideCTA) => {
    setRotationStopped(true);
    if (cta.analyticsEvent) {
      trackEvent('cta_click', 'engagement', cta.analyticsEvent);
    }
    if (cta.action === 'navigate' && cta.destination) {
      navigate(cta.destination);
    } else if (cta.action === 'dialog') {
      if (cta.dialogType === 'billConcern') {
        setConcernDialogOpen(true);
      } else if (cta.dialogType === 'joinTeam') {
        onJoinTeamOpen();
      } else {
        onCalendlyOpen();
      }
    }
  };

  // ── Touch swipe handler (react-swipeable) ────────────────────────────────────
  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => {
      setRotationStopped(true);
      setActiveSlide((prev) => (prev + 1) % SLIDE_COUNT);
    },
    onSwipedRight: () => {
      setRotationStopped(true);
      setActiveSlide((prev) => (prev - 1 + SLIDE_COUNT) % SLIDE_COUNT);
    },
    preventScrollOnSwipe: true,
    trackMouse: false,
    delta: 50,
  });

  // ── Dot click handler ────────────────────────────────────────────────────────
  const handleDotClick = (index: number) => {
    setActiveSlide(index);
    setRotationStopped(true);
  };

  const renderMobileSlides = () => (
    <Box sx={{ display: 'grid' }} aria-live="polite" aria-atomic="false">
      {slides.map((slide, index) => {
        const isActive = activeSlide === index;
        return (
          <Box
            key={slide.id}
            role="group"
            aria-roledescription="slide"
            aria-label={`Slide ${index + 1} of ${SLIDE_COUNT}: ${slide.heading}`}
            aria-hidden={!isActive}
            sx={{
              gridArea: '1 / 1',
              minWidth: 0,
              visibility: isActive ? 'visible' : 'hidden',
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: '#111111',
              color: '#ffffff',
            }}
          >
            <Box
              component="img"
              src={slide.mobileImage || slide.desktopImage}
              alt={slide.imageAlt}
              sx={{
                width: '100%',
                height: { xs: 168, sm: 240 },
                objectFit: 'cover',
                objectPosition: index === 0 ? '25% 30%' : 'center 35%',
                backgroundColor: '#080808',
              }}
            />
            <Box sx={{ px: { xs: 3, sm: 5 }, pt: 3, pb: 1, maxWidth: 680, width: '100%', mx: 'auto' }}>
              <Typography
                component="p"
                sx={{ color: '#FFD700', fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.1em', mb: 1.5 }}
              >
                {slide.subheading || (index === 1 ? 'Community & action' : 'Support the movement')}
              </Typography>
              <Typography
                component={index === 0 ? 'h1' : 'h2'}
                sx={{
                  fontSize: { xs: 'clamp(1.75rem, 7.5vw, 2.25rem)', sm: '2.5rem' },
                  fontWeight: 800,
                  lineHeight: 1.16,
                  letterSpacing: '-0.025em',
                  textWrap: 'balance',
                  mb: 2,
                }}
              >
                {slide.heading}
              </Typography>
              <Typography component="p" sx={{ fontSize: '1rem', lineHeight: 1.65, color: '#dedede', mb: 3 }}>
                {slide.bodyText}
              </Typography>
              <Stack spacing={1}>
                {slide.ctas.map((cta) => (
                  <Button
                    key={cta.label}
                    variant={cta.variant === 'outlined' ? 'text' : 'contained'}
                    tabIndex={isActive ? 0 : -1}
                    aria-haspopup={cta.action === 'dialog' ? 'dialog' : undefined}
                    onClick={() => handleCtaClick(cta)}
                    startIcon={cta.icon}
                    sx={{
                      minHeight: 48,
                      px: 2,
                      py: 1.25,
                      fontSize: '0.9375rem',
                      lineHeight: 1.4,
                      textTransform: 'none',
                      ...(cta.variant === 'outlined'
                        ? { color: '#fff', '&:hover': { backgroundColor: '#ffffff14' } }
                        : { background: '#FFD700', color: '#111', boxShadow: 'none' }),
                      '&:focus-visible': { outline: '2px solid #FFD700', outlineOffset: 3 },
                    }}
                  >
                    {cta.label}
                  </Button>
                ))}
              </Stack>
            </Box>
          </Box>
        );
      })}
    </Box>
  );

  // ── Slide 1: desktop background-image layout (same pattern as original hero)
  const renderSlide1 = () => {
    const slide = slides[0];
    const isActive = activeSlide === 0;
    return (
      <Box
        key={slide.id}
        role="group"
        aria-roledescription="slide"
        aria-label={`Slide 1 of ${SLIDE_COUNT}: ${slide.heading}`}
        aria-hidden={!isActive}
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          opacity: isActive ? 1 : 0,
          transition: `opacity ${FADE_MS}ms ease-in-out`,
          zIndex: isActive ? 1 : 0,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'flex-end',
          padding: { xs: 4, md: 6, lg: 10, xl: 16 },
          backgroundImage: (t) => ({
            xs: `url(${slide.desktopImage})`,
            md: `linear-gradient(to left, ${t.palette.background.default}, transparent), url(${slide.desktopImage})`,
          }),
          backgroundPosition: { xs: '25% top', md: 'center left' },
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
        }}
      >
        {/* Mobile gradient overlay: black bottom-to-transparent, behind text */}
        <Box
          sx={{
            display: { xs: 'block', md: 'none' },
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: '100%',
            height: '70%',
            background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, transparent 100%)',
            zIndex: 0,
            pointerEvents: 'none',
          }}
        />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            maxWidth: { xs: '100%', md: '65%' },
            zIndex: 1,
          }}
        >
          <Typography
            variant="h1"
            component="h1"
            gutterBottom
            sx={(t) => ({
              fontWeight: 800,
              fontSize: { md: '2.25rem', lg: '2.75rem', xl: '3.25rem' },
              lineHeight: 1.16,
              mb: 2,
              color: { xs: '#fff', md: t.palette.mode === 'dark' ? '#fff' : '#000' },
              textShadow: {
                xs: '0 2px 8px rgba(0,0,0,0.7)',
                md: '0 2px 6px rgba(0,0,0,0.18), 0 1px 2px rgba(0,0,0,0.12)',
              },
            })}
          >
            {slide.heading}
          </Typography>
          {slide.subheading && (
            <Typography
              variant="h2"
              component="h2"
              sx={(t) => ({
                fontWeight: 600,
                fontSize: { md: '1.125rem', lg: '1.375rem' },
                lineHeight: '1.4',
                mb: 2,
                color: { xs: '#FFD700', md: t.palette.mode === 'dark' ? '#FFD700' : '#7a5c00' },
                textShadow: { xs: '0 1px 3px rgba(0,0,0,0.6)', md: '0 1px 4px rgba(0,0,0,0.15)' },
              })}
            >
              {slide.subheading}
            </Typography>
          )}
          <Typography
            variant="h3"
            component="p"
            sx={(t) => ({
              fontWeight: 400,
              fontSize: { md: '1.125rem', lg: '1.25rem' },
              lineHeight: 1.65,
              mb: 4,
              color: { xs: '#fff', md: t.palette.mode === 'dark' ? '#fff' : '#000' },
              textShadow: { xs: '0 1px 4px rgba(0,0,0,0.7)', md: '0 1px 4px rgba(0,0,0,0.15)' },
            })}
          >
            {slide.bodyText}
          </Typography>
          <Stack
            direction={isMobile ? 'column' : 'row'}
            spacing={2}
            sx={{ width: '100%', flexWrap: 'wrap', gap: 2 }}
            useFlexGap
          >
            {slide.ctas.map((cta) => (
              <Button
                key={cta.label}
                variant={cta.variant || 'contained'}
                tabIndex={isActive ? 0 : -1}
                aria-haspopup={cta.dialogType === 'billConcern' ? 'dialog' : undefined}
                size="large"
                onClick={() => handleCtaClick(cta)}
                sx={{
                  px: { xs: 2, md: 3 },
                  py: 1.5,
                  width: { xs: '100%', sm: 'auto' },
                  ...(cta.variant === 'outlined' && {
                    color: { xs: 'common.white', md: 'text.primary' },
                    borderColor: 'currentColor',
                    backgroundColor: { xs: 'rgba(0,0,0,0.3)', md: 'background.paper' },
                    '&:hover': {
                      borderColor: 'primary.main',
                      backgroundColor: 'background.paper',
                      color: 'text.primary',
                    },
                  }),
                }}
                startIcon={cta.icon}
              >
                {cta.label}
              </Button>
            ))}
          </Stack>
        </Box>
      </Box>
    );
  };

  // ── Slide 2: desktop background-image layout OR mobile stacked layout ────────
  const renderSlide2 = () => {
    const slide = slides[1];
    const isActive = activeSlide === 1;
    const cta = slide.ctas[0];

    // Desktop: text left, image absolutely positioned flush to bottom-right corner
    return (
      <Box
        key={slide.id}
        role="group"
        aria-roledescription="slide"
        aria-label={`Slide 2 of ${SLIDE_COUNT}: ${slide.heading}`}
        aria-hidden={!isActive}
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          opacity: isActive ? 1 : 0,
          transition: `opacity ${FADE_MS}ms ease-in-out`,
          zIndex: isActive ? 1 : 0,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'flex-start',
          px: { md: 6, lg: 10, xl: 16 },
          backgroundColor: 'background.default',
          backgroundImage:
            'linear-gradient(315deg, rgba(255, 215, 0, 0.7) 0%, rgba(255, 215, 0, 0.16) 35%, rgba(255, 215, 0, 0) 60%)',
          overflow: 'hidden',
        }}
      >
        {/* Text content – left side */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', maxWidth: '50%', zIndex: 1 }}>
          <Typography
            variant="h1"
            component="h2"
            sx={{ fontWeight: 800, fontSize: { md: '2.25rem', lg: '2.75rem' }, lineHeight: 1.16, mb: 2 }}
          >
            {slide.heading}
          </Typography>
          <Typography
            variant="h3"
            component="p"
            sx={{ fontWeight: 400, fontSize: { md: '1.125rem', lg: '1.25rem' }, lineHeight: 1.65, mb: 4 }}
          >
            {slide.bodyText}
          </Typography>
          <Button
            variant="contained"
            size="large"
            tabIndex={isActive ? 0 : -1}
            onClick={() => handleCtaClick(cta)}
            sx={{ px: 4, py: 2 }}
            startIcon={cta.icon}
          >
            {cta.label}
          </Button>
        </Box>
        {/* Image – flush to bottom-right corner of the carousel */}
        <Box
          component="img"
          src={slide.desktopImage}
          alt={slide.imageAlt}
          sx={{
            position: 'absolute',
            bottom: 0,
            right: 0,
            height: '72vh',
            width: 'auto',
            objectFit: 'contain',
            objectPosition: 'bottom right',
          }}
        />
      </Box>
    );
  };

  const renderSlide3 = () => {
    const slide = slides[2];
    const isActive = activeSlide === 2;
    const cta = slide.ctas[0];

    return (
      <Box
        key={slide.id}
        role="group"
        aria-roledescription="slide"
        aria-label={`Slide 3 of ${SLIDE_COUNT}: ${slide.heading}`}
        aria-hidden={!isActive}
        sx={{
          position: 'absolute',
          inset: 0,
          opacity: isActive ? 1 : 0,
          transition: `opacity ${FADE_MS}ms ease-in-out`,
          zIndex: isActive ? 1 : 0,
          display: 'flex',
          alignItems: { xs: 'flex-end', md: 'center' },
          px: { xs: 4, md: 6, lg: 10, xl: 16 },
          py: { xs: 8, md: 4 },
          color: 'common.white',
          backgroundColor: '#03060a',
          backgroundImage: { xs: `url(${slide.mobileImage})`, md: `url(${slide.desktopImage})` },
          backgroundPosition: { xs: 'center top', md: 'center' },
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
        }}
      >
        <Box sx={{ maxWidth: { xs: '100%', md: '48%' }, textAlign: { xs: 'center', md: 'left' }, zIndex: 1 }}>
          <Typography
            component="h2"
            variant="h1"
            sx={{
              fontWeight: 800,
              fontSize: { md: '2.25rem', lg: '2.75rem' },
              lineHeight: 1.16,
              mb: 2,
              textShadow: '0 2px 18px #000',
            }}
          >
            {slide.heading}
          </Typography>
          <Typography
            component="p"
            variant="h3"
            sx={{
              fontWeight: 400,
              fontSize: { md: '1.125rem', lg: '1.25rem' },
              lineHeight: 1.65,
              mb: 4,
              textShadow: '0 2px 12px #000',
            }}
          >
            {slide.bodyText}
          </Typography>
          <Button
            variant="contained"
            size="large"
            tabIndex={isActive ? 0 : -1}
            onClick={() => handleCtaClick(cta)}
            startIcon={cta.icon}
            sx={{ px: 4, py: 2, width: { xs: '100%', md: 'auto' } }}
          >
            {cta.label}
          </Button>
        </Box>
      </Box>
    );
  };

  // Visible indicators with full-size touch targets and native keyboard behavior.
  const renderDots = () => (
    <Box
      sx={{
        position: { xs: 'relative', md: 'absolute' },
        bottom: { md: 16 },
        left: { md: '50%' },
        transform: { md: 'translateX(-50%)' },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: { xs: 1, md: 0 },
        zIndex: 10,
        backgroundColor: { xs: '#111', md: 'rgba(0,0,0,0.8)' },
        borderRadius: { md: 8 },
      }}
    >
      {slides.map((slide, i) => (
        <Box
          component="button"
          type="button"
          key={slide.id}
          aria-label={`Go to slide ${i + 1}: ${slide.heading}`}
          aria-pressed={activeSlide === i}
          onClick={() => handleDotClick(i)}
          sx={{
            width: 44,
            height: 44,
            display: 'grid',
            placeItems: 'center',
            border: 0,
            background: 'transparent',
            cursor: 'pointer',
            '&::after': {
              content: '""',
              width: activeSlide === i ? 24 : 8,
              height: 8,
              borderRadius: 4,
              backgroundColor: activeSlide === i ? '#FFD700' : '#999',
            },
            '&:focus-visible': { outline: '2px solid #FFD700', outlineOffset: -4, borderRadius: 2 },
          }}
        />
      ))}
      {!isTabletOrBelow && !prefersReducedMotion && (
        <Button
          onClick={() => setRotationStopped((stopped) => !stopped)}
          aria-label={rotationStopped ? 'Start slide rotation' : 'Pause slide rotation'}
          sx={{ minWidth: 44, minHeight: 44, color: '#fff' }}
        >
          {rotationStopped ? <PlayArrowIcon /> : <PauseIcon />}
        </Button>
      )}
    </Box>
  );

  // ── Carousel wrapper (FR-013, FR-015) ────────────────────────────────────────
  return (
    <>
      <Box
        role="region"
        aria-label="Featured highlights carousel"
        aria-roledescription="carousel"
        sx={{
          position: 'relative',
          flex: 1,
          minHeight: { md: 640 },
          overflow: 'hidden',
          marginX: 0,
        }}
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
        onFocus={() => setHasFocus(true)}
        onBlur={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setHasFocus(false);
        }}
        {...swipeHandlers}
      >
        {isTabletOrBelow ? (
          renderMobileSlides()
        ) : (
          <>
            {renderSlide1()}
            {renderSlide2()}
            {renderSlide3()}
          </>
        )}
        {renderDots()}
      </Box>
      <BillConcernDialog open={concernDialogOpen} onClose={() => setConcernDialogOpen(false)} />
    </>
  );
};

export default HeroCarousel;
